import {
  defaultParseSearch,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router";
import { CLEAR_FLAG } from "./clear-flag";
import { asPositiveInt } from "./utils";

// A `context` search param is the path + search the user was on before we bounced them to
// /login or /unresponsive, captured as one URL string. Its search was written by the router's
// own stringifier, so it has to be read back with the router's own parser: URLSearchParams
// hands back raw strings, and stringifySearch re-quotes any string that happens to parse as
// JSON -- so state=["ready"] would return as state="[\"ready\"]", gaining a layer of encoding
// on every round trip. `__clear` drops the bounce page's own params (context, reason, ...)
// instead of letting retainSearchParamsWithClear carry them onto the destination.
export const parseContext = (
  context: string | undefined,
  fallback = "/dashboard",
) => {
  const [to, searchStr] = (context ?? fallback).split("?", 2);
  return {
    to,
    search: {
      [CLEAR_FLAG]: true,
      context: undefined,
      ...(searchStr ? defaultParseSearch(searchStr) : {}),
    } as Record<string, any>,
  };
};

export type SearchMiddleware<TSearchSchema extends object> = ReturnType<
  typeof retainSearchParams<TSearchSchema>
>;
export function retainSearchParamsWithClear<
  TSearchSchema extends object,
>(): SearchMiddleware<TSearchSchema> {
  return ({ search, next }) => {
    const { [CLEAR_FLAG]: clear, ...result } = next(search) as TSearchSchema & {
      [CLEAR_FLAG]?: boolean;
    };
    if (clear) return result as TSearchSchema;
    return { ...search, ...result } as TSearchSchema;
  };
}

// Shared by /dashboard and /admin-dashboard -- both routes carry an `accountId` search param.
export type AccountIdSearch = { collapsed?: boolean; accountId?: number };

// Omits the `accountId` key entirely (rather than setting it to `undefined`) whenever there's no
// valid value -- accountIdSearchMiddlewares below only backfills a key that's actually *missing*
// from its output, not one present-but-undefined, so stamping an explicit `undefined` here would
// silently defeat it on every navigation, not just a genuinely fresh URL. A malformed value
// (non-numeric, decimal, negative) is treated the same as absent, so a canonicalization redirect
// can repair it.
export const validateAccountIdSearch = (s: unknown): AccountIdSearch => {
  const { accountId: incomingAccountId, ...rest } = (s ?? {}) as Record<
    string,
    unknown
  >;
  const accountId = asPositiveInt(incomingAccountId);
  return accountId !== undefined ? { ...rest, accountId } : rest;
};

export const accountIdSearchMiddlewares = [
  retainSearchParams<AccountIdSearch>(["accountId"]),
];

// A fresh URL with no accountId yet -- canonicalize it onto the given account (the user's own
// default) so every subsequent URL carries an explicit accountId via accountIdSearchMiddlewares
// above, rather than relying on a per-tab cache to remember it. Fires once per fresh URL, not on
// every load. No-op if accountId is already present or there's no account to canonicalize onto.
export const canonicalizeAccountIdSearch = (
  currentSearch: AccountIdSearch,
  accountId: number | undefined,
) => {
  if (currentSearch.accountId === undefined && accountId !== undefined) {
    throw redirect({
      to: ".",
      search: { ...currentSearch, accountId },
      replace: true,
    });
  }
};

const getSortField = (value: unknown): unknown => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return trimmed;
    }
    try {
      return getSortField(JSON.parse(trimmed));
    } catch {
      return undefined;
    }
  }
  if (typeof value === "object" && value !== null && "field" in value) {
    const direction = "dir" in value ? value.dir : undefined;
    if (
      direction !== undefined &&
      direction !== "asc" &&
      direction !== "desc"
    ) {
      return undefined;
    }
    return value.field;
  }
  return undefined;
};

export function sanitizeSortBy<TField extends string, TValue>(
  value: TValue,
  allowedFields: readonly TField[],
): TValue | undefined {
  const isArray = Array.isArray(value);
  const values = isArray ? value : value === undefined ? [] : [value];
  const sanitized = values.filter((item) =>
    allowedFields.includes(getSortField(item) as TField),
  );

  if (sanitized.length === 0) return undefined;
  return (isArray ? sanitized : sanitized[0]) as TValue;
}

// Parsed values, keyed by storage key, so a signal only reads and parses on the first get and on
// the first get after a write. The cache lives here, in a module every chunk imports once, rather
// than on the signal object: `autoCodeSplitting` copies a route file's top-level code into both
// halves of the split, so `const signal = makeLocalStorageSignal(...)` in a route file runs twice
// -- once in the chunk holding validateSearch/beforeLoad, once in the lazily loaded component
// chunk. Two objects, one storage key. With the cache on the object, the component half's writes
// updated only its own copy, and the route half kept serving the value it read on first entry
// until a reload -- the filter the user had just changed or cleared came straight back on the next
// visit (ID-6913). Keying the cache by storage key makes both copies share it.
const localStorageSignalCache = new Map<
  string,
  { valid: boolean; value?: unknown }
>();

// Another tab writing the same key leaves this one's parsed copy stale. `storage` only fires in
// the tabs that did NOT write, so this drops the cache rather than fighting with setValue. A null
// key means the whole store was cleared.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.storageArea && event.storageArea !== window.localStorage) return;
    if (event.key === null) {
      localStorageSignalCache.forEach((entry) => (entry.valid = false));
      return;
    }
    const entry = localStorageSignalCache.get(event.key);
    if (entry) {
      entry.valid = false;
    }
  });
}

export const makeLocalStorageSignal = <T>({
  key,
  defaultValue,
}: {
  key: string;
  defaultValue?: T;
}) => {
  let entry = localStorageSignalCache.get(key);
  if (!entry) {
    entry = { valid: false };
    localStorageSignalCache.set(key, entry);
  }
  const cache = entry;

  return {
    key,
    getValue: (): T | undefined => {
      if (cache.valid) return cache.value as T | undefined;
      const item = localStorage.getItem(key);
      let value = defaultValue;
      if (item !== null) {
        try {
          value = JSON.parse(item) as T;
        } catch (ex) {
          value = defaultValue;
        }
      }
      cache.value = value;
      cache.valid = true;
      return value;
    },
    setValue: (value: T) => {
      cache.value = value;
      cache.valid = true;
      if (value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    },
    invalidate: () => {
      cache.valid = false;
    },
  };
};
export type Signal<T = unknown> = ReturnType<typeof makeLocalStorageSignal<T>>;

// Seeds a filter the user last used (kept in localStorage by the table's onQueryArgumentChange)
// onto the URL, but ONLY when the route is first entered. Doing this from `validateSearch` instead
// -- which runs on every search change, not just on entry -- makes the stored value a second
// source of truth that outranks the URL: any navigation whose search legitimately omits the key
// (clearing the filter, a `__clear` link, the /login bounce round trip) gets the old value stamped
// straight back in, so clearing a filter appears to do nothing until a reload (ID-6913). Reading
// it once on entry keeps the "sticky filter" behaviour while leaving the URL authoritative for
// every navigation after that -- including the one that clears it.
export const seedPersistedSearch = <TSearch extends Record<string, any>>(
  signal: { getValue: () => Partial<TSearch> | undefined },
  {
    search,
    cause,
    location,
    keys,
    sanitize,
  }: {
    search: TSearch;
    cause: "preload" | "enter" | "stay";
    // The destination being loaded - redirecting to its own pathname (rather than to ".", which
    // resolves against wherever the router currently is) keeps a deep entry like
    // /devices/$deviceId/overview on the row it names instead of bouncing to the bare list.
    location?: { pathname: string };
    keys: (keyof TSearch)[];
    sanitize?: Partial<Record<keyof TSearch, (value: any) => any>>;
  },
) => {
  if (cause !== "enter") return;
  const saved = signal.getValue();
  if (!saved) return;

  const seed: Partial<TSearch> = {};
  for (const key of keys) {
    // A key already present in the URL wins -- the link, or the user, was explicit about it.
    if (search?.[key] !== undefined) continue;
    const sanitizer = sanitize?.[key];
    const value = sanitizer ? sanitizer(saved[key]) : saved[key];
    if (value !== undefined) {
      seed[key] = value;
    }
  }
  if (Object.keys(seed).length === 0) return;

  throw redirect({
    to: location?.pathname ?? ".",
    search: { ...search, ...seed },
    replace: true,
  });
};
